import React from 'react';
import { Navbar } from './Navbar';
import { Footer } from './Footer';
import { ArrowRightIcon, PhoneIcon, StarIcon, ShieldCheckIcon, SparklesIcon, LeafIcon, HomeIcon, BuildingIcon, DropletIcon, SunIcon, TrashIcon, SquareIcon, CheckCircleIcon } from 'lucide-react';
const ALL_CITIES = [{
  name: 'Ahwatukee Foothills',
  short: 'Ahwatukee',
  slug: 'ahwatukee-foothills'
}, {
  name: 'Casa Grande',
  short: 'Casa Grande',
  slug: 'casa-grande'
}, {
  name: 'Chandler',
  short: 'Chandler',
  slug: 'chandler'
}, {
  name: 'Gilbert',
  short: 'Gilbert',
  slug: 'gilbert'
}, {
  name: 'Litchfield Park',
  short: 'Litchfield Park',
  slug: 'litchfield-park'
}, {
  name: 'Maricopa',
  short: 'Maricopa',
  slug: 'maricopa'
}, {
  name: 'Mesa',
  short: 'Mesa',
  slug: 'mesa'
}, {
  name: 'Phoenix',
  short: 'Phoenix',
  slug: 'phoenix'
}, {
  name: 'Queen Creek',
  short: 'Queen Creek',
  slug: 'queen-creek'
}, {
  name: 'Scottsdale',
  short: 'Scottsdale',
  slug: 'scottsdale'
}, {
  name: 'Sun Lakes',
  short: 'Sun Lakes',
  slug: 'sun-lakes'
}, {
  name: 'Tempe',
  short: 'Tempe',
  slug: 'tempe'
}];
const SERVICES = [{
  title: 'Backyard Cleaning',
  slug: 'backyard-cleaning',
  desc: 'Complete backyard restoration including debris removal, patio washing, and outdoor furniture cleaning.',
  icon: HomeIcon
}, {
  title: 'Commercial Cleaning',
  slug: 'commercial-cleaning',
  desc: 'Professional exterior cleaning for storefronts, offices, and commercial properties.',
  icon: BuildingIcon
}, {
  title: 'Driveway Cleaning',
  slug: 'driveway-cleaning',
  desc: 'Remove oil stains, tire marks, and years of grime from concrete, pavers, and asphalt.',
  icon: DropletIcon
}, {
  title: 'Paver Cleaning',
  slug: 'paver-cleaning',
  desc: 'Restore the beauty of your pavers with deep cleaning, weed removal, and optional sealing.',
  icon: SquareIcon
}, {
  title: 'Solar Panel Cleaning',
  slug: 'solar-panel-cleaning',
  desc: 'Boost energy efficiency with safe, streak-free solar panel cleaning using deionized water.',
  icon: SunIcon
}, {
  title: 'Trash Can Cleaning',
  slug: 'trash-can-cleaning',
  desc: 'Sanitize and deodorize your bins with eco-friendly high-pressure cleaning.',
  icon: TrashIcon
}, {
  title: 'Window Cleaning',
  slug: 'window-cleaning',
  desc: 'Crystal-clear, streak-free windows inside and out, including screens, sills, and tracks.',
  icon: SparklesIcon
}];
const TESTIMONIALS = [{
  name: 'Sarah M.',
  initials: 'SM',
  text: 'Blue Cactus did an incredible job on our driveway and patio. It looks brand new! The team was professional, on time, and the results exceeded our expectations.'
}, {
  name: 'David R.',
  initials: 'DR',
  text: "Best power washing service we've ever used. They cleaned our entire home exterior and it looks like the day we moved in. Highly recommend!"
}, {
  name: 'Jennifer L.',
  initials: 'JL',
  text: "We've been using Blue Cactus for our commercial property for over a year. Always reliable, always spotless. Our customers notice the difference."
}];
const headingStyle = {
  color: '#0283C7',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 900 as const,
  lineHeight: 1.2
};
const bodyStyle = {
  color: '#025A8A',
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  lineHeight: 1.8
};
const subHeadingStyle = {
  color: '#0283C7',
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 800 as const,
  lineHeight: 1.3
};
const btnStyle = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 700 as const
};
interface LocationPageProps {
  city: string;
  cityShort: string;
  slug: string;
}
export function LocationPageTemplate({
  city,
  cityShort,
  slug
}: LocationPageProps) {
  const otherCities = ALL_CITIES.filter(c => c.slug !== slug);
  return <div className="w-full min-h-screen" style={{
    fontFamily: 'Inter, sans-serif'
  }} data-id="element-393">
      <Navbar data-id="element-394" />
      <main data-id="element-395">
        {/* ===== Section 1 — Hero ===== */}
        <section className="w-full py-20 md:py-28 px-4" style={{
        backgroundColor: '#0283C7'
      }} data-id="element-396">
          <div className="max-w-4xl mx-auto text-center" data-id="element-397">
            <p className="uppercase tracking-[0.2em] mb-4 text-sm" style={{
            color: 'rgba(255,255,255,0.85)',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700
          }} data-id="element-398">
              Serving {city}, AZ
            </p>
            <h1 className="text-white font-bold mb-6" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(28px, 5vw, 48px)',
            lineHeight: 1.15
          }} data-id="element-399">
              Power Washing Services in {city}, AZ
            </h1>
            <p className="mb-10 mx-auto max-w-2xl" style={{
            color: 'rgba(255,255,255,0.9)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            lineHeight: 1.7
          }} data-id="element-400">
              Blue Cactus Power Washing provides top-quality residential and
              commercial exterior cleaning to homes and businesses across {city}
              , Arizona.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-401">
              <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
              backgroundColor: '#ffffff',
              color: '#0283C7',
              ...btnStyle
            }} data-id="element-402">
                Get a Free Quote
              </a>
              <a href="tel:4805998442" className="inline-flex items-center justify-center gap-2 w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:bg-white/10" style={{
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '2px solid #ffffff',
              ...btnStyle
            }} data-id="element-403">
                <PhoneIcon className="h-4 w-4" data-id="element-404" />
                Call Now: (480) 599-8442
              </a>
            </div>
            {/* Trust strip */}
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm" style={{
            color: 'rgba(255,255,255,0.8)',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-405">
              <span className="flex items-center gap-2" data-id="element-406">
                <ShieldCheckIcon className="h-4 w-4" data-id="element-407" /> Licensed • Bonded •
                Insured
              </span>
              <span className="flex items-center gap-1" data-id="element-408">
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-409" />
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-410" />
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-411" />
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-412" />
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-413" />
                <span className="ml-1" data-id="element-414">150+ Five-Star Reviews</span>
              </span>
            </div>
          </div>
        </section>

        {/* ===== Section 2 — Intro (Split Layout) ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-415">
          <div className="max-w-7xl mx-auto" data-id="element-416">
            <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-417">
              <div className="w-full lg:w-1/2" data-id="element-418">
                <div className="rounded-2xl overflow-hidden" style={{
                boxShadow: '0 20px 60px rgba(2,131,199,0.15)'
              }} data-id="element-419">
                  <img src="https://cdn.magicpatterns.com/uploads/u5rvnHTQ8C2zZ747ot3d8a/lpw-service-driveway-cleaning-488x650.webp" alt={`Power washing service in ${city}`} className="w-full h-auto object-cover" style={{
                  maxHeight: '500px'
                }} data-id="element-420" />
                </div>
              </div>
              <div className="w-full lg:w-1/2" data-id="element-421">
                <h2 className="font-bold mb-6" style={{
                ...headingStyle,
                fontSize: 'clamp(24px, 4vw, 30px)'
              }} data-id="element-422">
                  #1 Rated Power Washing in {cityShort}
                </h2>
                <p className="mb-6" style={bodyStyle} data-id="element-423">
                  Blue Cactus Power Washing is a professional exterior cleaning
                  service provider serving {city}, AZ. We offer power washing,
                  window cleaning, and exterior cleaning for both residential
                  and commercial properties. Our team uses modern equipment and
                  eco-friendly cleaning products to deliver high-quality
                  results. Whether it's your driveway, patio, storefront, or
                  entire building exterior — we restore your property's original
                  beauty.
                </p>
                <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-white text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
                backgroundColor: '#0283C7',
                ...btnStyle
              }} data-id="element-424">
                  Get a Free Quote
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Section 3 — Services Available ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#E8F4FD'
      }} data-id="element-425">
          <div className="max-w-7xl mx-auto" data-id="element-426">
            <h2 className="font-bold text-center mb-14" style={{
            ...headingStyle,
            fontSize: 'clamp(24px, 4vw, 36px)'
          }} data-id="element-427">
              Our Services in {cityShort}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-428">
              {SERVICES.map(service => {
              const Icon = service.icon;
              return <a key={service.slug} href={`/services/${service.slug}`} className="bg-white rounded-2xl p-8 flex flex-col gap-4 transition-all hover:shadow-lg hover:scale-[1.01]" style={{
                boxShadow: '0 4px 20px rgba(2,131,199,0.08)'
              }} data-id="element-429">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#E8F4FD'
                }} data-id="element-430">
                      <Icon className="h-6 w-6" style={{
                    color: '#0283C7'
                  }} data-id="element-431" />
                    </div>
                    <h3 className="font-bold" style={{
                  ...subHeadingStyle,
                  fontSize: '18px'
                }} data-id="element-432">
                      {service.title}
                    </h3>
                    <p style={{
                  ...bodyStyle,
                  fontSize: '14px'
                }} data-id="element-433">
                      {service.desc}
                    </p>
                    <span className="flex items-center gap-1 text-sm font-semibold mt-auto" style={{
                  color: '#0283C7',
                  fontFamily: 'Inter, sans-serif'
                }} data-id="element-434">
                      Learn More <ArrowRightIcon className="h-4 w-4" data-id="element-435" />
                    </span>
                  </a>;
            })}
            </div>
            {/* CTA */}
            <div className="mt-12 flex justify-center" data-id="element-436">
              <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-white text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
              backgroundColor: '#0283C7',
              ...btnStyle
            }} data-id="element-437">
                Get a Free Quote
              </a>
            </div>
          </div>
        </section>

        {/* ===== Section 4 — Benefits ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-438">
          <div className="max-w-7xl mx-auto" data-id="element-439">
            <h2 className="font-bold text-center mb-14" style={{
            ...headingStyle,
            fontSize: 'clamp(24px, 4vw, 36px)'
          }} data-id="element-440">
              Why {cityShort} Homeowners Choose Blue Cactus
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-441">
              <div className="text-center p-8 rounded-2xl" style={{
              backgroundColor: '#E8F4FD'
            }} data-id="element-442">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{
                backgroundColor: '#0283C7'
              }} data-id="element-443">
                  <SparklesIcon className="h-7 w-7 text-white" data-id="element-444" />
                </div>
                <h3 className="font-bold mb-3" style={{
                ...subHeadingStyle,
                fontSize: '20px'
              }} data-id="element-445">
                  Restore Curb Appeal
                </h3>
                <p style={{
                ...bodyStyle,
                fontSize: '15px'
              }} data-id="element-446">
                  High-pressure cleaning removes years of dirt, grime, and
                  stains — making your property look brand new.
                </p>
              </div>
              <div className="text-center p-8 rounded-2xl" style={{
              backgroundColor: '#E8F4FD'
            }} data-id="element-447">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{
                backgroundColor: '#0283C7'
              }} data-id="element-448">
                  <ShieldCheckIcon className="h-7 w-7 text-white" data-id="element-449" />
                </div>
                <h3 className="font-bold mb-3" style={{
                ...subHeadingStyle,
                fontSize: '20px'
              }} data-id="element-450">
                  Protect Your Investment
                </h3>
                <p style={{
                ...bodyStyle,
                fontSize: '15px'
              }} data-id="element-451">
                  Regular power washing prevents damage from mold, mildew, and
                  buildup that can degrade surfaces over time.
                </p>
              </div>
              <div className="text-center p-8 rounded-2xl" style={{
              backgroundColor: '#E8F4FD'
            }} data-id="element-452">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6" style={{
                backgroundColor: '#0283C7'
              }} data-id="element-453">
                  <LeafIcon className="h-7 w-7 text-white" data-id="element-454" />
                </div>
                <h3 className="font-bold mb-3" style={{
                ...subHeadingStyle,
                fontSize: '20px'
              }} data-id="element-455">
                  Eco-Friendly &amp; Safe
                </h3>
                <p style={{
                ...bodyStyle,
                fontSize: '15px'
              }} data-id="element-456">
                  We use environmentally friendly cleaning products that are
                  safe for your family, pets, and landscaping.
                </p>
              </div>
            </div>
            {/* CTA */}
            <div className="mt-12 flex justify-center" data-id="element-457">
              <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-white text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
              backgroundColor: '#0283C7',
              ...btnStyle
            }} data-id="element-458">
                Get a Free Quote
              </a>
            </div>
          </div>
        </section>

        {/* ===== Section 5 — How It Works ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#0283C7'
      }} data-id="element-459">
          <div className="max-w-5xl mx-auto" data-id="element-460">
            <h2 className="text-white font-bold text-center mb-14" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(24px, 4vw, 36px)',
            lineHeight: 1.2
          }} data-id="element-461">
              How It Works
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10" data-id="element-462">
              <div className="text-center" data-id="element-463">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.4)'
              }} data-id="element-464">
                  <span className="text-white font-bold text-xl" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-465">
                    1
                  </span>
                </div>
                <h3 className="text-white font-bold mb-3" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                fontSize: '20px'
              }} data-id="element-466">
                  Free Quote
                </h3>
                <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                lineHeight: 1.7
              }} data-id="element-467">
                  Tell us about your property and we'll send a same-day
                  estimate.
                </p>
              </div>
              <div className="text-center" data-id="element-468">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.4)'
              }} data-id="element-469">
                  <span className="text-white font-bold text-xl" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-470">
                    2
                  </span>
                </div>
                <h3 className="text-white font-bold mb-3" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                fontSize: '20px'
              }} data-id="element-471">
                  We Show Up
                </h3>
                <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                lineHeight: 1.7
              }} data-id="element-472">
                  Our team arrives on time with commercial-grade equipment,
                  ready to work.
                </p>
              </div>
              <div className="text-center" data-id="element-473">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5" style={{
                backgroundColor: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.4)'
              }} data-id="element-474">
                  <span className="text-white font-bold text-xl" style={{
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-475">
                    3
                  </span>
                </div>
                <h3 className="text-white font-bold mb-3" style={{
                fontFamily: 'Montserrat, sans-serif',
                fontWeight: 800,
                fontSize: '20px'
              }} data-id="element-476">
                  Spotless Results
                </h3>
                <p style={{
                color: 'rgba(255,255,255,0.85)',
                fontFamily: 'Inter, sans-serif',
                fontSize: '15px',
                lineHeight: 1.7
              }} data-id="element-477">
                  Walk your property with us — if you're not happy, we make it
                  right.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ===== Section 6 — Testimonials ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#ffffff'
      }} data-id="element-478">
          <div className="max-w-7xl mx-auto" data-id="element-479">
            <h2 className="font-bold text-center mb-14" style={{
            ...headingStyle,
            fontSize: 'clamp(24px, 4vw, 36px)'
          }} data-id="element-480">
              What {cityShort} Customers Are Saying
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-481">
              {TESTIMONIALS.map((t, i) => <div key={i} className="bg-white rounded-2xl p-8" style={{
              borderLeft: '4px solid #0283C7',
              boxShadow: '0 4px 20px rgba(2,131,199,0.08)'
            }} data-id="element-482">
                  <div className="flex items-center gap-4 mb-4" data-id="element-483">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{
                  backgroundColor: '#0283C7',
                  fontFamily: 'Montserrat, sans-serif'
                }} data-id="element-484">
                      {t.initials}
                    </div>
                    <div data-id="element-485">
                      <p className="font-bold" style={{
                    color: '#0283C7',
                    fontFamily: 'Montserrat, sans-serif',
                    fontSize: '16px'
                  }} data-id="element-486">
                        {t.name}
                      </p>
                      <div className="flex gap-0.5" data-id="element-487">
                        {[...Array(5)].map((_, j) => <StarIcon key={j} className="h-4 w-4 fill-yellow-400 text-yellow-400" data-id="element-488" />)}
                      </div>
                    </div>
                  </div>
                  <p style={{
                ...bodyStyle,
                fontSize: '15px'
              }} data-id="element-489">
                    "{t.text}"
                  </p>
                </div>)}
            </div>
            <div className="mt-8 text-center" data-id="element-490">
              <a href="#reviews" className="text-sm font-semibold inline-flex items-center gap-1" style={{
              color: '#0283C7',
              fontFamily: 'Inter, sans-serif'
            }} data-id="element-491">
                See All Reviews <ArrowRightIcon className="h-4 w-4" data-id="element-492" />
              </a>
            </div>
            {/* CTA */}
            <div className="mt-10 flex justify-center" data-id="element-493">
              <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-white text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
              backgroundColor: '#0283C7',
              ...btnStyle
            }} data-id="element-494">
                Get a Free Quote
              </a>
            </div>
          </div>
        </section>

        {/* ===== Section 7 — Other Locations ===== */}
        <section className="w-full py-20 px-4" style={{
        backgroundColor: '#E8F4FD'
      }} data-id="element-495">
          <div className="max-w-5xl mx-auto text-center" data-id="element-496">
            <h2 className="font-bold mb-10" style={{
            ...headingStyle,
            fontSize: 'clamp(24px, 4vw, 30px)'
          }} data-id="element-497">
              We Also Serve These Areas
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-498">
              {otherCities.map(c => <a key={c.slug} href={`/locations/${c.slug}`} className="bg-white rounded-lg px-4 py-3 font-semibold text-sm transition-all hover:shadow-md hover:scale-[1.02]" style={{
              color: '#0283C7',
              fontFamily: 'Inter, sans-serif',
              boxShadow: '0 2px 8px rgba(2,131,199,0.06)'
            }} data-id="element-499">
                  {c.name}, AZ
                </a>)}
            </div>
            <p className="mt-8 text-sm" style={{
            color: '#025A8A',
            fontFamily: 'Inter, sans-serif'
          }} data-id="element-500">
              Not sure if we cover your area? Call{' '}
              <a href="tel:4805998442" className="font-semibold underline" style={{
              color: '#0283C7'
            }} data-id="element-501">
                (480) 599-8442
              </a>
            </p>
          </div>
        </section>

        {/* ===== Section 8 — Bottom CTA Banner ===== */}
        <section className="w-full py-16 px-4" style={{
        backgroundColor: '#0283C7'
      }} data-id="element-502">
          <div className="max-w-4xl mx-auto text-center" data-id="element-503">
            <h2 className="text-white font-bold mb-4" style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 900,
            fontSize: 'clamp(24px, 4vw, 36px)',
            lineHeight: 1.2
          }} data-id="element-504">
              Ready for a Cleaner Property in {cityShort}?
            </h2>
            <p className="mb-10" style={{
            color: 'rgba(255,255,255,0.9)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '18px',
            lineHeight: 1.7
          }} data-id="element-505">
              Get your free, no-obligation quote today. Licensed, bonded, and
              insured.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-506">
              <a href="#quote" className="inline-block w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:opacity-90 hover:shadow-lg" style={{
              backgroundColor: '#ffffff',
              color: '#0283C7',
              ...btnStyle
            }} data-id="element-507">
                Get a Free Quote
              </a>
              <a href="tel:4805998442" className="inline-flex items-center justify-center gap-2 w-full sm:w-auto text-center px-10 py-4 rounded-lg text-base transition-all hover:bg-white/10" style={{
              backgroundColor: 'transparent',
              color: '#ffffff',
              border: '2px solid #ffffff',
              ...btnStyle
            }} data-id="element-508">
                <PhoneIcon className="h-4 w-4" data-id="element-509" />
                Call Now: (480) 599-8442
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer data-id="element-510" />
    </div>;
}